<script>
  import { appIconUrl } from "lib/util/appIcons.js";
  import { ComboBox, DataTable} from "carbon-components-svelte";
  import { Pagination, Tooltip, Button } from "paxray-components-svelte";
  import { MORE_ICON } from '@utils/icons.js';

  export let headers = [];
  export let rows = [];
  export let summary = {};
  export let page = 1;
  export let pageSize = 25;
  export let paginated = true;
  export let sortable = true;
  export let sortKey = "active_time_seconds";
  export let sortDirection = "descending";
  export let minWidth = "82rem";
  export let pageSizes = [10, 25, 50];
  export let getIconUrl = (row) =>
    appIconUrl(row?.application, { clearname: row?.clearname });
  export let variant = "software";
  export let formatProcessCell = (row, key) => row?.[key];
  export let onProcessAction = () => {};
  export let onLicenseUpdate = async () => {};
  export let licenseMatchCandidates = [];
  export let onLicenseMatch = async () => {};
  export let onLicenseUnmatch = async () => {};

  const editableLicenseFields = {
    license_version: {
      field: "license_version",
      label: "Version",
      type: "text",
    },
    purchased_quantity: {
      field: "purchased_quantity",
      label: "Licences",
      type: "number",
    },
    license_cost_pa: {
      field: "license_cost_pa",
      label: "Licence Cost (p.a.)",
      type: "number",
    },
    service_cost_pa: {
      field: "service_cost_pa",
      label: "Service Cost (p.a.)",
      type: "number",
    },
    renewal_date: {
      field: "renewal_date",
      label: "Renewal Date",
      type: "date",
    },
    assessment_note: {
      field: "assessment_note",
      label: "Assessment",
      type: "textarea",
    },
  };

  let activeLicenseEditor = null;
  let licenseEditSaving = false;
  let licenseEditError = "";
  let activeLicenseMatcher = null;
  let licenseMatchSearch = "";
  let selectedLicenseMatch = "";
  let licenseMatchSaving = false;
  let licenseMatchError = "";
  let activeLicenseUnmatcher = null;
  let licenseUnmatchSaving = false;
  let licenseUnmatchError = "";
  let previousSort = `${sortKey}:${sortDirection}`;

  // Neue Sortierung zeigt die Spitze der Liste, nicht die bisherige Seite.
  $: if (`${sortKey}:${sortDirection}` !== previousSort) {
    previousSort = `${sortKey}:${sortDirection}`;
    page = 1;
  }

  function formatDuration(seconds) {
    const hours = Number(seconds || 0) / 3600;
    if (hours >= 1000) {
      return `${new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
      }).format(hours / 1000)}k h`;
    }
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: hours < 10 ? 1 : 0,
    }).format(hours)} h`;
  }

  function formatShare(value, total) {
    const totalValue = Number(total || 0);
    const share = totalValue ? (Number(value || 0) / totalValue) * 100 : 0;
    if (share > 0 && share < 1) return "<1";
    return Math.round(share);
  }

  function formatPercentage(value) {
    const percentage = Number(value || 0);
    if (percentage > 0 && percentage < 1) return "<1";
    return Math.round(percentage);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat("en", { notation: "compact" }).format(
      Number(value || 0),
    );
  }

  function formatLicenseCost(value) {
    if (value === null || value === undefined || value === "") return "—";
    const formattedValue = new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
    return `${formattedValue} €`;
  }

  function isMissingLicenseInput(value) {
    return (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    );
  }

  function renewalDates(value) {
    return String(value || "")
      .split(",")
      .map((date) => date.trim())
      .filter(Boolean);
  }

  function isPastRenewalDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const renewalDate = new Date(`${value}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !Number.isNaN(renewalDate.getTime()) && renewalDate < today;
  }

  function formatRenewalDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const renewalDate = new Date(`${value}T00:00:00`);
    if (Number.isNaN(renewalDate.getTime())) return value;
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(renewalDate);
  }

  function formatAssessment(value) {
    const text = String(value || "");
    const characters = Array.from(text);
    return characters.length > 80
      ? `${characters.slice(0, 80).join("")}…`
      : text || "—";
  }

  function formatApplication(value) {
    const text = String(value || "");
    if (variant !== "license") return text;
    const characters = Array.from(text);
    return characters.length > 40
      ? `${characters.slice(0, 40).join("")}…`
      : text;
  }

  function licenseDifference(row) {
    const capacity = licenseCapacity(row);
    if (capacity === null || row?.users === null || row?.users === undefined) {
      return null;
    }
    const licences = Number(capacity);
    return Number.isFinite(licences) ? licences - Number(row.users || 0) : null;
  }

  function licenseCapacity(row) {
    if (
      row?.purchased_quantity !== null &&
      row?.purchased_quantity !== undefined
    ) {
      return row.purchased_quantity;
    }
    return row?.totallicenses ?? null;
  }

  function formatType(value) {
    return String(normalizeType(value) || "Unclassified")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function normalizeType(value) {
    const normalizedType = String(value || "")
      .trim()
      .toLowerCase();
    return normalizedType === "supportive" ? "support" : normalizedType;
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  function getOrganizationGroups(application) {
    return {
      departments: uniqueValues(application.departments || []),
      teams: uniqueValues(application.teams || []),
    };
  }

  function organizationCount(application) {
    return getOrganizationGroups(application).teams.length;
  }

  function hasLowUserShare(application) {
    const totalUsers = Number(summary.users || 0);
    return totalUsers > 0 && Number(application.users || 0) / totalUsers < 0.1;
  }

  // Fehlt das Icon (Endpunkt liefert keins), zeigt der Platzhalter den
  // Anfangsbuchstaben der Anwendung statt des Browser-Symbols fuer ein kaputtes Bild.
  function appInitial(name) {
    const text = String(name || "").trim();
    return text ? text[0].toUpperCase() : "?";
  }

  function handleAppIconError(event) {
    event.currentTarget.classList.add("app-icon-hidden");
  }

  function licensePositions(row) {
    return (row?.licenses || []).filter((license) => license?.id);
  }

  function canEditLicenseCell(key) {
    return Boolean(editableLicenseFields[key]);
  }

  function isLicenseEditorOpen(row, key, editor) {
    return editor?.editorKey === licenseEditorKey(row, key);
  }

  function licenseEditorKey(row, key) {
    return `${row?.app_mapping_id || row?.application || "license"}:${key}`;
  }

  function licenseFieldValue(license, key) {
    const field = editableLicenseFields[key]?.field;
    const value = field ? license?.[field] : "";
    return value === null || value === undefined ? "" : String(value);
  }

  function startLicenseEdit(row, key) {
    const positions = licensePositions(row);
    const selectedLicense = positions.length === 1 ? positions[0] : null;
    activeLicenseEditor = {
      editorKey: licenseEditorKey(row, key),
      key,
      row,
      licenseId: selectedLicense?.id || "",
      value: selectedLicense
        ? licenseFieldValue(selectedLicense, key)
        : positions.length
          ? ""
          : licenseFieldValue(row, key),
    };
    licenseEditError = "";
  }

  function selectLicensePosition(event) {
    const licenseId = event.currentTarget.value;
    const selectedLicense = licensePositions(activeLicenseEditor?.row).find(
      (license) => String(license.id) === licenseId,
    );
    activeLicenseEditor = {
      ...activeLicenseEditor,
      licenseId,
      value: selectedLicense
        ? licenseFieldValue(selectedLicense, activeLicenseEditor.key)
        : "",
    };
    licenseEditError = "";
  }

  function licensePositionLabel(license, index) {
    const details = [
      license.name,
      license.license_version,
      license.license_type,
    ]
      .filter(Boolean)
      .join(" · ");
    return details || `Position ${index + 1}`;
  }

  function cancelLicenseEdit() {
    if (licenseEditSaving) return;
    activeLicenseEditor = null;
    licenseEditError = "";
  }

  async function saveLicenseEdit() {
    if (!activeLicenseEditor || licenseEditSaving) return;
    const positions = licensePositions(activeLicenseEditor.row);
    if (positions.length > 1 && !activeLicenseEditor.licenseId) {
      licenseEditError = "Select a licence position first.";
      return;
    }

    const config = editableLicenseFields[activeLicenseEditor.key];
    let value = activeLicenseEditor.value;
    if (config.type === "number") {
      if (value === "") {
        value = null;
      } else {
        value = Number(value);
        if (!Number.isFinite(value) || value < 0) {
          licenseEditError = "Enter a non-negative number.";
          return;
        }
      }
    } else {
      value = String(value || "").trim() || null;
    }
    licenseEditSaving = true;
    licenseEditError = "";
    try {
      await onLicenseUpdate({
        licenseId: activeLicenseEditor.licenseId || null,
        field: config.field,
        value,
        row: activeLicenseEditor.row,
      });
      activeLicenseEditor = null;
    } catch (error) {
      licenseEditError = error.message || "The value could not be saved.";
    } finally {
      licenseEditSaving = false;
    }
  }

  function normalizeMatchName(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function licenseMatchKey(candidate) {
    return String(candidate?.app_mapping_id || candidate?.application || "");
  }

  function licenseMatchScore(sourceName, candidateName) {
    const source = normalizeMatchName(sourceName);
    const candidate = normalizeMatchName(candidateName);
    if (!source || !candidate) return 0;
    if (source === candidate) return 1000;
    let score =
      source.includes(candidate) || candidate.includes(source) ? 300 : 0;
    const sourceTokens = new Set(
      source.split(" ").filter((token) => token.length > 2),
    );
    for (const token of candidate.split(" ")) {
      if (token.length > 2 && sourceTokens.has(token)) score += 50;
    }
    return score;
  }

  function matchingCandidates(row, search) {
    const query = normalizeMatchName(search);
    return [...licenseMatchCandidates]
      .filter(
        (candidate) =>
          !query || normalizeMatchName(candidate.application).includes(query),
      )
      .sort(
        (a, b) =>
          licenseMatchScore(row.application, b.application) -
            licenseMatchScore(row.application, a.application) ||
          String(a.application || "").localeCompare(
            String(b.application || ""),
          ),
      );
  }

  function licenseMatchItems(row) {
    return matchingCandidates(row, "").map((candidate) => ({
      id: licenseMatchKey(candidate),
      text: licenseMatchLabel(candidate),
    }));
  }

  function shouldFilterLicenseMatchItem(item, value) {
    const query = normalizeMatchName(value);
    return !query || normalizeMatchName(item?.text).includes(query);
  }

  function licenseMatchLabel(candidate) {
    const details = [];
    if (candidate.users !== null && candidate.users !== undefined) {
      details.push(
        `${candidate.users} user${Number(candidate.users) === 1 ? "" : "s"}`,
      );
    }
    if (candidate.teams?.length) {
      details.push(
        `${candidate.teams.length} team${candidate.teams.length === 1 ? "" : "s"}`,
      );
    }
    return [candidate.application, ...details].join(" · ");
  }

  function startLicenseMatch(row) {
    activeLicenseMatcher = {
      rowKey: licenseEditorKey(row, "match"),
      row,
    };
    licenseMatchSearch = "";
    selectedLicenseMatch = "";
    licenseMatchError = "";
  }

  function isLicenseMatcherOpen(row, matcher) {
    return matcher?.rowKey === licenseEditorKey(row, "match");
  }

  function cancelLicenseMatch() {
    if (licenseMatchSaving) return;
    activeLicenseMatcher = null;
    licenseMatchSearch = "";
    selectedLicenseMatch = "";
    licenseMatchError = "";
  }

  async function saveLicenseMatch() {
    if (!activeLicenseMatcher || !selectedLicenseMatch || licenseMatchSaving)
      return;
    const candidate = licenseMatchCandidates.find(
      (item) => licenseMatchKey(item) === selectedLicenseMatch,
    );
    if (!candidate) {
      licenseMatchError = "Select an observed tool.";
      return;
    }
    licenseMatchSaving = true;
    licenseMatchError = "";
    try {
      await onLicenseMatch({ row: activeLicenseMatcher.row, candidate });
      activeLicenseMatcher = null;
      selectedLicenseMatch = "";
    } catch (error) {
      licenseMatchError = error.message || "The software could not be matched.";
    } finally {
      licenseMatchSaving = false;
    }
  }

  function startLicenseUnmatch(row) {
    activeLicenseUnmatcher = {
      rowKey: licenseEditorKey(row, "unmatch"),
      row,
    };
    licenseUnmatchError = "";
  }

  function isLicenseUnmatcherOpen(row, unmatcher) {
    return unmatcher?.rowKey === licenseEditorKey(row, "unmatch");
  }

  function cancelLicenseUnmatch() {
    if (licenseUnmatchSaving) return;
    activeLicenseUnmatcher = null;
    licenseUnmatchError = "";
  }

  async function removeLicenseMatch() {
    if (!activeLicenseUnmatcher || licenseUnmatchSaving) return;
    licenseUnmatchSaving = true;
    licenseUnmatchError = "";
    try {
      await onLicenseUnmatch({ row: activeLicenseUnmatcher.row });
      activeLicenseUnmatcher = null;
    } catch (error) {
      licenseUnmatchError =
        error.message || "The software match could not be removed.";
    } finally {
      licenseUnmatchSaving = false;
    }
  }
</script>

<div
  class="table-scroll"
  class:process-table={variant === "process"}
  class:license-table={variant === "license"}
  style={`--software-table-min-width: ${minWidth};`}
>
  <DataTable
    size="compact"
    {headers}
    {rows}
    {sortable}
    selectable={false}
    radio={false}
    batchSelection={false}
    {page}
    {pageSize}
    bind:sortKey
    bind:sortDirection
  >
    <svelte:fragment slot="cell" let:cell let:row>
      {#if variant === "process" && cell.key === "actions"}
        <div class="actions-button-wrap">
          <Button
            kind="secondary"
            size="small"
            icon={MORE_ICON}
            iconDescription={`Open actions for ${row.l4}`}
            on:click={() => onProcessAction(row)}
          />
        </div>
      {:else if variant === "process" && cell.key === "type"}
        <span class="type-badge process-{normalizeType(row.type)}"
          >{formatType(row.type)}</span
        >
      {:else if variant === "process" && cell.key === "teams"}
        <span class="relation-count">
          <strong>{(row.teams || []).length}</strong>
          {#if row.teams?.length}
            <Tooltip>
              <div class="relation-tooltip">
                {#each row.teams as team}<span>{team}</span>{/each}
              </div>
            </Tooltip>
          {/if}
        </span>
      {:else if variant === "process" && cell.key === "projects"}
        <span class="relation-count">
          <strong>{(row.projects || []).length}</strong>
          {#if row.projects?.length}
            <Tooltip>
              <div class="relation-tooltip">
                {#each row.projects as project}<span>{project}</span>{/each}
              </div>
            </Tooltip>
          {/if}
        </span>
      {:else if variant === "process" && cell.key === "l1"}
        <strong>{row.l1 || "–"}</strong>
      {:else if variant === "process"}
        <span class:na-value={!formatProcessCell(row, cell.key)}
          >{formatProcessCell(row, cell.key) || "N/A"}</span
        >
      {:else if variant === "license" && editableLicenseFields[cell.key]}
        <div class="editable-license-cell">
          {#if isLicenseEditorOpen(row, cell.key, activeLicenseEditor)}
            <div class="license-cell-editor">
              {#if licensePositions(row).length > 1}
                <label>
                  <span>Licence position</span>
                  <select
                    value={activeLicenseEditor.licenseId}
                    on:change={selectLicensePosition}
                    disabled={licenseEditSaving}
                  >
                    <option value="">Select position</option>
                    {#each licensePositions(row) as license, index}
                      <option value={license.id}
                        >{licensePositionLabel(license, index)}</option
                      >
                    {/each}
                  </select>
                </label>
              {/if}

              {#if licensePositions(row).length <= 1 || activeLicenseEditor.licenseId}
                <label>
                  <span>{editableLicenseFields[cell.key].label}</span>
                  {#if editableLicenseFields[cell.key].type === "textarea"}
                    <textarea
                      rows="3"
                      bind:value={activeLicenseEditor.value}
                      disabled={licenseEditSaving}
                    ></textarea>
                  {:else if editableLicenseFields[cell.key].type === "number"}
                    <input
                      type="number"
                      min="0"
                      step={cell.key === "purchased_quantity" ? "1" : "0.01"}
                      bind:value={activeLicenseEditor.value}
                      disabled={licenseEditSaving}
                      on:keydown={(event) => {
                        if (event.key === "Enter") saveLicenseEdit();
                        if (event.key === "Escape") cancelLicenseEdit();
                      }}
                    />
                  {:else if editableLicenseFields[cell.key].type === "date"}
                    <input
                      type="date"
                      bind:value={activeLicenseEditor.value}
                      disabled={licenseEditSaving}
                      on:keydown={(event) => {
                        if (event.key === "Enter") saveLicenseEdit();
                        if (event.key === "Escape") cancelLicenseEdit();
                      }}
                    />
                  {:else}
                    <input
                      type="text"
                      bind:value={activeLicenseEditor.value}
                      disabled={licenseEditSaving}
                      on:keydown={(event) => {
                        if (event.key === "Enter") saveLicenseEdit();
                        if (event.key === "Escape") cancelLicenseEdit();
                      }}
                    />
                  {/if}
                </label>
              {/if}

              {#if licenseEditError}
                <span class="license-edit-error">{licenseEditError}</span>
              {/if}
              <div class="license-editor-actions">
                <button
                  type="button"
                  class="save"
                  on:click={saveLicenseEdit}
                  disabled={licenseEditSaving ||
                    (licensePositions(row).length > 1 &&
                      !activeLicenseEditor.licenseId)}
                >
                  {licenseEditSaving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  on:click={cancelLicenseEdit}
                  disabled={licenseEditSaving}>Cancel</button
                >
              </div>
            </div>
          {:else}
            <div class="license-cell-value">
              {#if isMissingLicenseInput(row[cell.key])}
                <span class="license-input-missing">Missing</span>
              {:else if cell.key === "application"}
                <div class="application-name">
                  <img
                    src={getIconUrl(row)}
                    alt=""
                    on:error={handleAppIconError}
                  />
                  <span class="app-initial" aria-hidden="true"
                    >{appInitial(row.application)}</span
                  >
                  <strong title={row.application}
                    >{formatApplication(row.application)}</strong
                  >
                </div>
              {:else if cell.key === "purchased_quantity"}
                {#if isMissingLicenseInput(row.purchased_quantity)}
                  <span class="license-input-missing">Missing</span>
                {:else}
                  {row.purchased_quantity}
                {/if}
              {:else if cell.key === "license_version"}
                {row.license_version || "—"}
              {:else if cell.key === "license_cost_pa"}
                {#if isMissingLicenseInput(row.license_cost_pa)}
                  <span class="license-input-missing">Missing</span>
                {:else}
                  {formatLicenseCost(row.license_cost_pa)}
                {/if}
              {:else if cell.key === "service_cost_pa"}
                {#if isMissingLicenseInput(row.service_cost_pa)}
                  <span class="license-input-missing">Missing</span>
                {:else}
                  {formatLicenseCost(row.service_cost_pa)}
                {/if}
              {:else if cell.key === "renewal_date"}
                {#if row.renewal_date}
                  <span class="renewal-dates">
                    {#each renewalDates(row.renewal_date) as renewalDate, index}
                      {#if index > 0}<span class="renewal-separator">•</span
                        >{/if}
                      <span
                        class="renewal-date"
                        class:renewal-expired={isPastRenewalDate(renewalDate)}
                        >{formatRenewalDate(renewalDate)}</span
                      >
                    {/each}
                  </span>
                {:else}
                  —
                {/if}
              {:else if cell.key === "assessment_note"}
                <span class="assessment-cell" title={row.assessment_note || ""}
                  >{formatAssessment(row.assessment_note)}</span
                >
              {/if}
            </div>
            {#if canEditLicenseCell(cell.key)}
              <span class="license-edit-trigger">
                <button
                  type="button"
                  title={`Edit ${editableLicenseFields[cell.key].label}`}
                  aria-label={`Edit ${editableLicenseFields[cell.key].label}`}
                  on:click|stopPropagation={() =>
                    startLicenseEdit(row, cell.key)}
                >
                  <img
                    src="/img/assistant/icons/edit.svg"
                    alt=""
                    aria-hidden="true"
                  />
                </button>
              </span>
            {/if}
          {/if}
        </div>
      {:else if cell.key === "application"}
        <div class:license-application-cell={variant === "license"}>
          <div class="application-name">
            <img src={getIconUrl(row)} alt="" on:error={handleAppIconError} />
            <span class="app-initial" aria-hidden="true"
              >{appInitial(row.application)}</span
            >
            <strong title={row.application}
              >{formatApplication(row.application)}</strong
            >
            {#if variant === "license" && row.license_status === "imported_and_matched" && row.matched_application}
              <span class="license-match-tooltip">
                <Tooltip direction="top" align="start"
                  >Matched to: {row.matched_application}</Tooltip
                >
              </span>
              <span class="license-unmatch-trigger">
                <button
                  type="button"
                  title="Edit software match"
                  aria-label={`Edit software match for ${row.application}`}
                  on:click|stopPropagation={() => startLicenseUnmatch(row)}
                >
                  <img
                    src="/img/assistant/icons/edit.svg"
                    alt=""
                    aria-hidden="true"
                  />
                </button>
              </span>
            {/if}
          </div>
          {#if variant === "license" && row.license_status === "imported_matching_needed"}
            {#if isLicenseMatcherOpen(row, activeLicenseMatcher)}
              <div class="license-match-editor">
                <ComboBox
                  size="sm"
                  titleText="Match with observed tool"
                  placeholder="Search or select a tool"
                  items={licenseMatchItems(row)}
                  bind:selectedId={selectedLicenseMatch}
                  bind:value={licenseMatchSearch}
                  shouldFilterItem={shouldFilterLicenseMatchItem}
                  disabled={licenseMatchSaving}
                />
                {#if licenseMatchError}
                  <span class="license-edit-error">{licenseMatchError}</span>
                {/if}
                <div class="license-editor-actions">
                  <button
                    type="button"
                    class="save"
                    on:click={saveLicenseMatch}
                    disabled={!selectedLicenseMatch || licenseMatchSaving}
                  >
                    {licenseMatchSaving ? "Matching…" : "Match"}
                  </button>
                  <button
                    type="button"
                    on:click={cancelLicenseMatch}
                    disabled={licenseMatchSaving}>Cancel</button
                  >
                </div>
              </div>
            {:else}
              <div class="license-match-actions">
                <span class="license-match-status">Not matched</span>
                <button
                  type="button"
                  class="license-match-button"
                  on:click|stopPropagation={() => startLicenseMatch(row)}
                  >Match</button
                >
              </div>
            {/if}
          {:else if variant === "license" && row.license_status === "imported_and_matched" && isLicenseUnmatcherOpen(row, activeLicenseUnmatcher)}
            <div class="license-unmatch-editor">
              <span class="license-unmatch-message"
                >Remove the match with <strong>{row.matched_application}</strong
                >?</span
              >
              {#if licenseUnmatchError}
                <span class="license-edit-error">{licenseUnmatchError}</span>
              {/if}
              <div class="license-editor-actions">
                <button
                  type="button"
                  class="danger"
                  on:click={removeLicenseMatch}
                  disabled={licenseUnmatchSaving}
                >
                  {licenseUnmatchSaving ? "Removing…" : "Remove match"}
                </button>
                <button
                  type="button"
                  on:click={cancelLicenseUnmatch}
                  disabled={licenseUnmatchSaving}>Cancel</button
                >
              </div>
            </div>
          {/if}
        </div>
      {:else if cell.key === "category"}
        <span
          class:software-category={variant === "software"}
          class:uncategorized-tag={variant === "software" &&
            String(row.category || "")
              .trim()
              .toLowerCase() === "uncategorized"}
        >
          {row.category || "N/A"}
        </span>
      {:else if cell.key === "type"}
        <span class="type-badge {normalizeType(row.type)}"
          >{formatType(row.type)}</span
        >
      {:else if cell.key === "totallicenses"}
        {row.totallicenses ?? "—"}
      {:else if cell.key === "purchased_quantity"}
        {row.purchased_quantity ?? "—"}
      {:else if cell.key === "license_version"}
        {row.license_version || "—"}
      {:else if cell.key === "license_cost_pa"}
        {formatLicenseCost(row.license_cost_pa)}
      {:else if cell.key === "service_cost_pa"}
        {formatLicenseCost(row.service_cost_pa)}
      {:else if cell.key === "renewal_date"}
        {#if row.renewal_date}
          <span class="renewal-dates">
            {#each renewalDates(row.renewal_date) as renewalDate, index}
              {#if index > 0}<span class="renewal-separator">•</span>{/if}
              <span
                class="renewal-date"
                class:renewal-expired={isPastRenewalDate(renewalDate)}
                >{formatRenewalDate(renewalDate)}</span
              >
            {/each}
          </span>
        {:else}
          —
        {/if}
      {:else if cell.key === "costperlicense"}
        {formatLicenseCost(row.costperlicense)}
      {:else if cell.key === "subscriptiontype"}
        {row.subscriptiontype || "—"}
      {:else if cell.key === "active_time_seconds"}
        <span class="metric-share-cell">
          <span
            class="metric-share-value"
            class:metric-share-low={formatShare(
              row.active_time_seconds,
              summary.active_time_seconds,
            ) === "<1"}
            >{formatShare(
              row.active_time_seconds,
              summary.active_time_seconds,
            )}%</span
          >
          <Tooltip>Absolute: {formatDuration(row.active_time_seconds)}</Tooltip>
        </span>
      {:else if cell.key === "users"}
        {#if variant === "license"}
          {#if isMissingLicenseInput(row.users)}
            <span class="license-input-missing">Missing</span>
          {:else}
            {formatNumber(row.users)}
          {/if}
        {:else}
          <span class:users-warning={hasLowUserShare(row)}>
            <span class="metric-share-cell">
              <span
                class="metric-share-value"
                class:metric-share-low={formatShare(
                  row.users,
                  summary.users,
                ) === "<1"}>{formatShare(row.users, summary.users)}%</span
              >
              <Tooltip>Absolute: {formatNumber(row.users)} users</Tooltip>
            </span>
          </span>
        {/if}
      {:else if variant === "license" && cell.key === "license_utilization"}
        {#if isMissingLicenseInput(row.users)}
          <span class="license-input-missing">Missing</span>
        {:else}
          {@const difference = licenseDifference(row)}
          {#if difference === null}
            <span class="license-input-missing">Missing</span>
          {:else}
            {@const licences = Number(licenseCapacity(row))}
            {@const utilization =
              licences > 0 ? (Number(row.users || 0) / licences) * 100 : null}
            {#if utilization === null}
              <span class="license-input-missing">Missing</span>
            {:else if utilization > 100}
              <span class="license-usage-warning"
                >{Math.round(utilization)}%</span
              >
            {:else}
              {Math.round(utilization)}%
            {/if}
          {/if}
        {/if}
      {:else if variant === "license" && cell.key === "license_difference"}
        {@const difference = licenseDifference(row)}
        {#if difference === null}
          <span class="license-input-missing">Missing</span>
        {:else}
          {formatNumber(difference)}
        {/if}
      {:else if variant === "license" && cell.key === "license_savings_cost"}
        {#if isMissingLicenseInput(row.users)}
          <span class="license-input-missing">Missing</span>
        {:else}
          {@const difference = licenseDifference(row)}
          {@const savingsCount =
            difference === null ? null : Math.max(difference, 0)}
          {#if savingsCount === null || isMissingLicenseInput(row.costperlicense)}
            <span class="license-input-missing">Missing</span>
          {:else}
            {formatLicenseCost(savingsCount * Number(row.costperlicense))}
          {/if}
        {/if}
      {:else if variant === "license" && cell.key === "assessment_note"}
        <span class="assessment-cell" title={row.assessment_note || ""}
          >{formatAssessment(row.assessment_note)}</span
        >
      {:else if cell.key === "organization"}
        {#if variant === "license" && isMissingLicenseInput(row.users)}
          <span class="license-input-missing">Missing</span>
        {:else}
          {@const organizationGroups = getOrganizationGroups(row)}
          <span
            class="organization-cell"
            class:organization-warning={organizationCount(row) <= 1}
          >
            <strong>{organizationCount(row)}</strong>
            {#if organizationGroups.departments.length || organizationGroups.teams.length}
              <Tooltip>
                <div class="organization-tooltip">
                  {#if organizationGroups.departments.length}
                    <strong>Departments</strong>
                    {#each organizationGroups.departments as department}
                      <span>{department}</span>
                    {/each}
                  {/if}
                  {#if organizationGroups.teams.length}
                    <strong>Teams</strong>
                    {#each organizationGroups.teams as team}
                      <span>{team}</span>
                    {/each}
                  {/if}
                </div>
              </Tooltip>
            {/if}
          </span>
        {/if}
      {:else if cell.key === "dependency_count"}
        <span class="dependency-cell">
          <strong>{row.dependency_count}</strong>
          {#if row.dependencies?.length}
            <Tooltip>
              <div class="dependency-tooltip">
                {#each row.dependencies.slice(0, 6) as dependency}
                  <div>
                    <strong>{dependency.application}</strong>
                    <span>
                      {formatNumber(dependency.transitions)} transitions ·
                      {formatPercentage(dependency.share)}%
                    </span>
                  </div>
                {/each}
              </div>
            </Tooltip>
          {/if}
        </span>
      {:else if cell.key === "copy_time_seconds"}
        <span class="metric-share-cell">
          <span
            class="metric-share-value"
            class:metric-share-low={formatShare(
              row.copy_time_seconds,
              summary.copy_time_seconds,
            ) === "<1"}
            >{formatShare(
              row.copy_time_seconds,
              summary.copy_time_seconds,
            )}%</span
          >
          <Tooltip>Absolute: {formatDuration(row.copy_time_seconds)}</Tooltip>
        </span>
      {:else if cell.key === "text_time_seconds"}
        <span class="metric-share-cell">
          <span
            class="metric-share-value"
            class:metric-share-low={formatShare(
              row.text_time_seconds,
              summary.text_time_seconds,
            ) === "<1"}
            >{formatShare(
              row.text_time_seconds,
              summary.text_time_seconds,
            )}%</span
          >
          <Tooltip>Absolute: {formatDuration(row.text_time_seconds)}</Tooltip>
        </span>
      {:else if cell.key === "clicks"}
        <span class="metric-share-cell">
          <span
            class="metric-share-value"
            class:metric-share-low={formatShare(row.clicks, summary.clicks) ===
              "<1"}>{formatShare(row.clicks, summary.clicks)}%</span
          >
          <Tooltip>Absolute: {formatNumber(row.clicks)}</Tooltip>
        </span>
      {:else}
        {cell.value || "—"}
      {/if}
    </svelte:fragment>
  </DataTable>
</div>
{#if paginated}
  <Pagination dark totalItems={rows.length} {pageSizes} bind:page bind:pageSize />
{/if}

<style>
  .table-scroll {
    min-height: 0;
    flex: 1;
    overflow: auto;
  }

  .table-scroll :global(.bx--data-table-container) {
    min-width: var(--software-table-min-width);
  }

  .license-table :global(.bx--data-table) {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }

  .table-scroll :global(.bx--data-table thead) {
    position: sticky;
    z-index: 2;
    top: 0;
  }

  .table-scroll :global(.bx--data-table th) {
    height: 2.3rem;
    padding: 0.42rem 0.65rem;
    background: var(--pax-black);
    color: var(--pax-white);
    text-align: left;
    vertical-align: middle;
  }

  .table-scroll :global(.bx--data-table th button),
  .table-scroll :global(.bx--data-table th button:hover),
  .table-scroll :global(.bx--table-sort),
  .table-scroll :global(.bx--table-sort:hover) {
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .table-scroll :global(.bx--data-table th button) {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    padding: 0;
    text-align: left;
  }

  .table-scroll :global(.bx--table-header-label),
  .table-scroll :global(.bx--table-sort__icon) {
    color: var(--pax-white);
  }

  .table-scroll :global(.bx--table-sort__icon path) {
    fill: var(--pax-white);
  }

  .table-scroll :global(.bx--data-table td) {
    height: 2.3rem;
    padding: 0.42rem 0.65rem;
    max-width: 12rem;
    vertical-align: middle;
    text-align: left;
    font-size: 0.78rem;
    border-bottom: 1px solid #e6e8eb !important;
  }

  .table-scroll :global(.bx--data-table td:has(.software-category)) {
    max-width: none;
    white-space: nowrap;
  }

  .license-table :global(.bx--data-table th),
  .license-table :global(.bx--data-table td) {
    width: 1%;
    max-width: none;
    white-space: nowrap;
  }

  .license-table :global(.bx--data-table th button) {
    min-width: max-content;
  }

  .table-scroll :global(.bx--data-table tbody tr:last-child td) {
    border-bottom: 0 !important;
  }

  .table-scroll :global(.bx--data-table tbody tr:hover td) {
    background: #f7f8fa;
  }

  .application-name {
    display: flex;
    width: 10.5rem;
    min-width: 0;
    max-width: 10.5rem;
    align-items: center;
    gap: 0.6rem;
    overflow: hidden;
  }

  .application-name > img {
    width: 1.35rem;
    height: 1.35rem;
    flex: 0 0 1.35rem;
    border-radius: 0.3rem;
    object-fit: contain;
  }

  .application-name > img {
    display: block;
  }

  .application-name > img:global(.app-icon-hidden) {
    display: none;
  }

  .application-name > .app-initial {
    display: none;
    flex: 0 0 auto;
    width: 1.35rem;
    height: 1.35rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
    background: var(--pax-grey-20, #e5e7eb);
    color: var(--pax-black, #111);
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
  }

  .application-name > img:global(.app-icon-hidden) + .app-initial {
    display: flex;
  }

  .application-name strong {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    color: var(--pax-black);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .license-table .application-name {
    width: max-content;
    max-width: none;
  }

  .license-table .application-name strong {
    max-width: none;
    overflow: visible;
    text-overflow: clip;
  }

  .license-application-cell {
    display: grid;
    gap: 0.35rem;
  }

  .license-match-actions {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .license-match-status {
    color: #6f6f6f;
    font-size: 0.7rem;
  }

  .license-match-tooltip {
    display: inline-flex;
    flex: 0 0 auto;
    color: #6f6f6f;
  }

  .license-unmatch-trigger {
    display: inline-flex;
    flex: 0 0 auto;
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease-in-out;
  }

  .application-name:hover .license-unmatch-trigger,
  .license-unmatch-trigger:focus-within {
    opacity: 1;
    pointer-events: auto;
  }

  .license-unmatch-trigger button {
    display: flex;
    width: 1.5rem;
    height: 1.5rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    background: var(--pax-black);
    cursor: pointer;
  }

  .license-unmatch-trigger img {
    width: 0.75rem;
    height: 0.75rem;
  }

  .license-match-status {
    padding: 0.2rem 0.45rem;
    border-radius: 2rem;
    background: #fff1f1;
    color: #a2191f;
  }

  .license-match-button {
    min-height: 1.65rem;
    padding: 0.25rem 0.55rem;
    border-radius: 0.25rem;
    background: var(--pax-black);
    color: var(--pax-white);
    cursor: pointer;
  }

  .editable-license-cell,
  .license-cell-value {
    display: flex;
    min-width: 0;
    align-items: center;
  }

  .editable-license-cell {
    position: relative;
    gap: 0.4rem;
  }

  .license-cell-value {
    flex: 0 1 auto;
  }

  .license-edit-trigger {
    display: inline-flex;
    flex: 0 0 auto;
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease-in-out;
  }

  .editable-license-cell:hover .license-edit-trigger,
  .license-edit-trigger:focus-within {
    opacity: 1;
    pointer-events: auto;
  }

  .license-edit-trigger button {
    display: flex;
    width: 1.5rem;
    height: 1.5rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    background: var(--pax-black);
    cursor: pointer;
  }

  .license-edit-trigger img {
    width: 0.75rem;
    height: 0.75rem;
  }

  .license-cell-editor,
  .license-match-editor,
  .license-unmatch-editor {
    display: grid;
    width: min(20rem, 70vw);
    gap: 0.45rem;
    padding: 0.55rem;
    border: 1px solid #c6c6c6;
    border-radius: 0.35rem;
    background: var(--pax-white);
    white-space: normal;
  }

  .license-unmatch-message {
    color: #393939;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .license-cell-editor label {
    display: grid;
    gap: 0.2rem;
  }

  .license-cell-editor label > span {
    color: #525252;
    font-size: 0.7rem;
  }

  .license-cell-editor input,
  .license-cell-editor select,
  .license-cell-editor textarea {
    width: 100%;
    min-height: 2rem;
    padding: 0.35rem 0.45rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.2rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font: inherit;
  }

  .license-match-editor :global(.bx--list-box),
  .license-match-editor :global(.bx--combo-box) {
    width: 100%;
  }

  .license-cell-editor textarea {
    resize: vertical;
  }

  .license-editor-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.35rem;
  }

  .license-editor-actions button {
    min-height: 1.8rem;
    padding: 0.3rem 0.6rem;
    border-radius: 0.25rem;
    background: #e8e8e8;
    color: var(--pax-black);
  }

  .license-editor-actions button.save {
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .license-editor-actions button.danger {
    background: #da1e28;
    color: var(--pax-white);
  }

  .license-editor-actions button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .license-edit-error {
    color: #a2191f;
    font-size: 0.7rem;
  }

  .type-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    border-radius: 2rem;
    background: #f4f4f4;
    color: var(--pax-black);
    font-size: 0.7rem;
    white-space: nowrap;
  }

  .type-badge.main {
    background: #b7e3bd;
    color: var(--pax-black);
  }

  .type-badge.communication {
    background: #abcfe8;
    color: var(--pax-black);
  }

  .type-badge.support {
    background: #e5e8a8;
    color: var(--pax-black);
  }

  .type-badge.unwanted {
    background: #e6a7ad;
    color: var(--pax-black);
  }

  .type-badge.process-management {
    background: #abcfe8;
    color: var(--pax-black);
  }

  .type-badge.process-core {
    background: #b7e3bd;
    color: var(--pax-black);
  }

  .type-badge.process-support {
    background: #e5e8a8;
    color: var(--pax-black);
  }

  .metric-share-value {
    cursor: help;
    color: var(--pax-black);
    white-space: nowrap;
  }

  .metric-share-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .metric-share-value.metric-share-low {
    color: #8a8a8a;
  }

  .dependency-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .dependency-cell > strong,
  .organization-cell > strong,
  :global(.metric-value strong) {
    font-weight: 400;
  }

  .organization-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .organization-cell.organization-warning > strong {
    padding: 0;
  }

  .organization-cell.organization-warning {
    padding: 0.25rem 0.5rem;
    background: #e6a7ad;
    color: var(--pax-black);
    border-radius: 2rem;
  }

  .users-warning .metric-share-value {
    padding: 0.25rem 0.5rem;
    border-radius: 2rem;
    background: #e6a7ad;
    color: var(--pax-black);
  }

  .renewal-dates {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 0.4rem;
    min-width: max-content;
  }

  .renewal-date {
    white-space: nowrap;
  }

  .renewal-separator {
    font-weight: 700;
  }

  .renewal-date.renewal-expired {
    padding: 0.25rem 0.5rem;
    border-radius: 2rem;
    background: #e6a7ad;
    color: var(--pax-black);
  }

  .uncategorized-tag,
  .license-input-missing,
  .license-usage-warning {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    border-radius: 2rem;
    background: #e6a7ad;
    color: var(--pax-black);
    font-size: 0.7rem;
    white-space: nowrap;
  }

  .license-usage-warning {
    background: var(--pax-state-warning-new);
  }

  .dependency-tooltip {
    display: grid;
    min-width: 15rem;
    gap: 0.55rem;
  }

  .dependency-tooltip div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .organization-tooltip {
    display: grid;
    min-width: 13rem;
    gap: 0.35rem;
  }

  .organization-tooltip > strong {
    margin-top: 0.35rem;
  }

  .organization-tooltip > strong:first-child {
    margin-top: 0;
  }

  .process-table :global(.bx--data-table tbody td:last-child) {
    text-align: center;
  }
  .actions-button-wrap {
    display: flex;
    justify-content: center;
  }
  .relation-count {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
  }
  .relation-count strong {
    font-weight: 400;
  }
  .na-value {
    color: #da1e28;
    font-weight: 700;
    text-transform: none;
  }

  .dependency-tooltip span {
    color: #c6c6c6;
    white-space: nowrap;
  }
</style>
